import { setClassroomPersonalisation } from "js/api/personalisationApi";
import _ from "lodash";
import * as clsApi from "js/api/classroomApi";
import useStore from "stores/index";
import { apiFields } from "./_utils";
import { CLASSROOM_SOURCE_TYPES } from "types/classrooms";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createClassroomStore = (set, get, api, setIn) => ({
	...apiFields(),
	dataV2: [],
	teacherClassroomsEnabled: false,
	teacherClassroomsSyncEnabled: false,
	teacherStableIdV1: null,
	teacherId: null,
	loaded: false,
	loadedClassDetails: false,
	fetchInProgress: false,
	showUnpinnedClasses: !(localStorage?.getItem("COLLAPSE_SECTION") === "true"),

	handleUpdatedV2: (result) => {
		// result contains classv2 response
		// We map result into classrooms for backward compatibility
		let classrooms = [];
		if (result.data) {
			classrooms = result.data.map((c) => {
				const classroom = Object.assign({}, c);

				if (!c.label) {
					classroom.label = _.get(c, "label") || _.get(c, "desc") || _.get(c, "name") || _.get(c, "zoom.group", "unnamed class");
				} else {
					classroom.label = c.provider_type === CLASSROOM_SOURCE_TYPES.GOOGLE ? c.label : c.desc;
				}

				classroom.sourceId = c.sourceId;

				return classroom;
			});
		}

		setIn({
			dataV2: classrooms,
			teacherClassroomsEnabled: result.teacher_classrooms_enabled,
			teacherClassroomsSyncEnabled: result.teacher_classrooms_sync_enabled,
			teacherStableIdV1: result.teacherStableIdV1,
			teacherId: result.teacherId,
			loaded: true,
			fetchInProgress: false,
		});
	},

	fetchClassrooms: async () => {
		if (get().classroom.fetchInProgress) {
			return;
		}

		setIn({ fetchInProgress: true });

		const data = await clsApi.Get("classrooms", "v2");
		get().classroom.handleUpdatedV2(data);
		useStore.getState().chat.refreshNotifications(!useStore.getState().chat.refreshNotificationsStatus);
	},

	handleAddClassroom: async (classroom) => {
		var response = {};
		try {
			await clsApi.Post(
				"classrooms",
				{
					...classroom,
					teacherId: get().classroom.teacherId,
					teacherStableIdV1: get().classroom.teacherStableIdV1,
				},
				(data) => {
					if (data && data.result) {
						get().classroom.addClassroomDataV2(data.result);
					}
				},
				(error) => {
					if (error && error.respJson && error.respJson.error) response.error = error.respJson.error;
					else response.error = "Failed to create a class";
				}
			);
		} catch (e) {
			response.error = e + "";
		}
		return response;
	},

	/**
	 * This is delete function
	 * @param {string} classroomStableId - Stable ID of the classroom to be deleted
	 * @returns Response from the API call returned to the UI
	 */
	handleDeleteClassroom: async (classroomStableId) => {
		await clsApi.DeleteV2(`v1/teachers/${get().classroom.getTeacherStableIdV1()}/classrooms/${classroomStableId}`);

		// Using hte zustand method to set values right after the endpoints are called successfully
		// This is make sure we handle the mutations in the right places i.e. after deleting a classroom is successful.
		setIn({
			dataV2: get().classroom.dataV2.filter((classroom) => classroom.stableIdV1 !== classroomStableId),
		});
	},

	getTeacherStableIdV1: () => {
		return get().classroom.teacherStableIdV1;
	},

	getTeacherId: () => {
		return get().classroom.teacherId;
	},

	getClassroomV2: (className) => {
		const classrooms = get().classroom.getClassrooms();
		for (const room of classrooms) {
			if (room.id === className || room.name === className || room.sourceId === className) {
				return room;
			}
		}

		return {};
	},

	getClassrooms: () => {
		return get().classroom.dataV2;
	},

	isActiveV2: (classroomId) => {
		const activeClass = get().classroom.getClassroomV2(classroomId);
		if (activeClass !== undefined && Object.entries(activeClass).length > 0) {
			const currentTime = new Date().getTime() / 1000;
			const sessionEndTime = activeClass.session?.sessionEndTime;
			if (activeClass.session?.isMonitoring) {
				if (sessionEndTime && sessionEndTime > currentTime) {
					return sessionEndTime - currentTime;
				}
				return false;
			} else {
				if (sessionEndTime && sessionEndTime > currentTime) {
					return sessionEndTime - currentTime;
				}

				return false;
			}
		}
		return false;
	},

	// check if class is a non local group aka maybe a google / azure sync group
	isClassSyncGroup: (currentClass) => {
		return !!currentClass.provider_type;
	},

	isClassSessionActiveV2: (currentClass) => {
		return currentClass && get().classroom.loaded && !!get().classroom.isActiveV2(currentClass);
	},

	updateClassroomDataV2: (classroom, transformToV2 = false) => {
		let updatedClassroomV2 = classroom;

		if (transformToV2) {
			const oldClassroom = get().classroom.getClassroomV2(classroom.groupId);

			updatedClassroomV2 = {
				...oldClassroom,
				name: oldClassroom.label || oldClassroom.name,
				sourceId: oldClassroom.sourceId,
				session: {
					...oldClassroom.session,
					inviteCode: classroom.invite_code,
					inviteCodeExpiry: classroom.invite_code_expiry,
				},
				teachers: classroom.classroomOwners,
				students: classroom.classroomUsers,
				memberCount: classroom.classroomUsers.length || 0,
			};

			const updatedClassroomsDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...updatedClassroomsDataV2, updatedClassroomV2],
			});
		} else {
			const { classroom: classroomData } = classroom;
			const oldClassroom = get().classroom.getClassroomV2(classroomData.id);

			updatedClassroomV2 = {
				...classroomData,
				...oldClassroom,
				// You would think you could just override the old session data with the new session data, but the objects are slightly different and it breaks the ending of a session...
				// No capacity to properly fix this right now, so I'll just add an override of the session data afterwards. Work done for CLASS-426.
				// Let's clean this up when we have capacity. Migrating this file to TS would be massively helpful here. Also the mix of this store and useClassroomDetailsQuery is a mess that doesn't help.
				session: classroomData.session || null, // override any session data with the new session data. If undefined, set to null because that's what it comes as in oldClassroom object if not in session.
				sourceId: oldClassroom.sourceId,
				label: classroomData.name,
				sourceType: oldClassroom.sourceType,
				memberCount: classroomData.students?.length || 0,
			};

			const updatedClassroomsDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...updatedClassroomsDataV2, updatedClassroomV2],
			});
		}
	},

	updateClassroomZoomV2: (classroom) => {
		const oldClassroom = get().classroom.getClassroomV2(classroom.classroomId);
		if (Object.entries(oldClassroom).length) {
			const updatedClassroomDataV2 = {
				...oldClassroom,
				session: {
					...oldClassroom.session,
					sessionEndTime: classroom.expiryUnix,
				},
			};

			const filteredClassroomDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...filteredClassroomDataV2, updatedClassroomDataV2],
			});
		}
	},

	updateClassroomStartTime: (classroomId, startTime) => {
		const oldClassroom = get().classroom.getClassroomV2(classroomId);
		if (Object.entries(oldClassroom).length) {
			const updatedClassroomDataV2 = {
				...oldClassroom,
				session: {
					...oldClassroom.session,
					sessionStartTime: startTime,
				},
			};

			const filteredClassroomDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...filteredClassroomDataV2, updatedClassroomDataV2],
			});
		}
	},

	removeClassroomV2: (classroomStableId) => {
		setIn({
			dataV2: get().classroom.dataV2.filter((filterClassroom) => filterClassroom.stableIdV1 !== classroomStableId),
		});
	},

	addClassroomDataV2: (data) => {
		const { classroom } = data;
		setIn({
			dataV2: [...get().classroom.dataV2, classroom],
		});
	},

	updateMonitorClassroomDataV2: (monitorData) => {
		const monitorClassroom = get().classroom.getClassroomV2(monitorData.classroomId);
		if (Object.entries(monitorClassroom).length) {
			const updatedMonitorClassroomV2 = {
				...monitorClassroom,
				session: {
					...monitorClassroom.session,
					isMonitoring: true,
					sessionEndTime: monitorData.expiryUnix,
				},
			};

			const filteredClassroomDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== monitorClassroom.id);
			setIn({
				dataV2: [...filteredClassroomDataV2, updatedMonitorClassroomV2],
			});
		}

		if (monitorData?.previousClassroomId) {
			const previousClassroom = get().classroom.getClassroomV2(monitorData.previousClassroomId);
			if (Object.entries(previousClassroom).length) {
				const updatedMonitorClassroomV2 = {
					...previousClassroom,
					session: {
						...previousClassroom.session,
						isMonitoring: true,
						sessionEndTime: 0,
					},
				};

				const filteredClassroomDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== previousClassroom.id);
				setIn({
					dataV2: [...filteredClassroomDataV2, updatedMonitorClassroomV2],
				});
			}
		}
	},

	setClassDetailsLoaded: (loaded) => {
		setIn({ loadedClassDetails: loaded });
	},

	setClassDetailsLoading: () => {
		setIn({ loadedClassDetails: false });
	},

	getPinnedClassrooms: () => {
		const classrooms = get().classroom.dataV2;
		const pinnedClassrooms = classrooms.filter((c) => c.personalisation && c.personalisation.pinned);
		return pinnedClassrooms;
	},

	getUnpinnedClassrooms: () => {
		const classrooms = get().classroom.dataV2;
		const unpinnedClassrooms = classrooms.filter((c) => !c.personalisation || !c.personalisation.pinned);
		return unpinnedClassrooms;
	},

	toggleClassroomPin: (id) => {
		const oldClassroom = get().classroom.getClassroomV2(id);

		setClassroomPersonalisation(
			get().classroom.teacherStableIdV1,
			oldClassroom.stableIdV1,
			!oldClassroom.personalisation?.pinned,
			oldClassroom.personalisation?.displayName,
			oldClassroom.personalisation?.studentSortOrder
		).then(() => {
			const updatedClassroomV2 = {
				...oldClassroom,
				personalisation: {
					...oldClassroom.personalisation,
					pinned: !oldClassroom.personalisation?.pinned,
				},
			};

			const updatedClassroomsDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...updatedClassroomsDataV2, updatedClassroomV2],
			});
		});
	},

	setStudentSortOrder: (id, studentSortOrder) => {
		const oldClassroom = get().classroom.getClassroomV2(id);
		setClassroomPersonalisation(
			get().classroom.teacherStableIdV1,
			oldClassroom.stableIdV1,
			oldClassroom.personalisation?.pinned,
			oldClassroom.personalisation?.displayName,
			studentSortOrder
		).then(() => {
			const updatedClassroomV2 = {
				...oldClassroom,
				personalisation: {
					...oldClassroom.personalisation,
					studentSortOrder,
				},
			};

			const updatedClassroomsDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

			setIn({
				dataV2: [...updatedClassroomsDataV2, updatedClassroomV2],
			});
		});
	},

	validateClassroomName: (displayName, classId = "") => {
		const classrooms = get().classroom.dataV2;
		return classrooms.some(
			(c) =>
				classId !== c.id &&
				(displayName === c?.personalisation?.displayName?.toLowerCase() ||
					displayName === c?.name?.toLowerCase() ||
					displayName === c?.sourceId?.toLowerCase() ||
					displayName === c?.description?.toLowerCase())
		);
	},

	setClassroomName: async (id, personalisation) => {
		const oldClassroom = get().classroom.getClassroomV2(id);

		return setClassroomPersonalisation(
			get().classroom.teacherStableIdV1,
			oldClassroom.stableIdV1,
			personalisation.pinned,
			personalisation.displayName
		)
			.then(() => {
				const updatedClassroomV2 = {
					...oldClassroom,
					personalisation: {
						...oldClassroom.personalisation,
						pinned: personalisation?.pinned,
						displayName: personalisation.displayName,
					},
				};

				const updatedClassroomsDataV2 = get().classroom.dataV2.filter((filterClassroom) => filterClassroom.id !== oldClassroom.id);

				setIn({
					dataV2: [...updatedClassroomsDataV2, updatedClassroomV2],
				});
			})
			.catch((e) => {
				throw e;
			});
	},
});

/**
 * @return {IClassroomStore}
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getClassroomStore = (state) => state.classroom;
